import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  Users as UsersIcon,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import { DriverDialog } from '@/components/drivers/driver-dialog'
import type { DriverRow } from '@/components/drivers/driver-types'
import { ConfirmDialog } from '@/components/shared/confirm-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type ApiClient, ApiError, type PaginatedResponse } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Drivers admin page.
 *
 * Same skeleton as Clients + Vehicles, with three driver-
 * specific twists:
 *
 *   1. **No DELETE.** Users are NEVER hard-deleted from the
 *      admin UI — they have FK-RESTRICT references on every
 *      mission and planning row. Instead, we expose
 *      "Désactiver" / "Réactiver" via PATCH (`active` flag).
 *      A soft-delete flow exists in the schema but is reserved
 *      for super_admin maintenance.
 *
 *   2. **Create goes through `/auth/register`**, not the users
 *      module. The auth module owns the password hashing path,
 *      so we keep registration centralised there.
 *
 *   3. **Role and password are not editable inline.** A future
 *      "Réinitialiser le mot de passe" action will mint a new
 *      hash and either email it or display it for transmission.
 *      Role changes deserve their own audited workflow.
 *
 * Filters: text search (name + email), role filter, status
 * filter.
 */

const PAGE_SIZE = 20
const QUERY_NS = 'users' as const

type RoleFilter = 'all' | 'admin' | 'driver'
type StatusFilter = 'all' | 'active' | 'inactive'

export function DriversPage() {
  const api = useApi()
  const queryClient = useQueryClient()

  /* -------- search (debounced) -------- */
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    if (searchDraft === search) return
    const t = setTimeout(() => setSearch(searchDraft), 300)
    return () => clearTimeout(t)
  }, [searchDraft, search])

  /* -------- filters -------- */
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  /* -------- pagination -------- */
  const [page, setPage] = useState(1)
  useEffect(() => {
    setPage(1)
  }, [search, roleFilter, statusFilter])

  /* -------- dialog state -------- */
  const [editing, setEditing] = useState<DriverRow | null | undefined>(undefined)
  const [pendingToggle, setPendingToggle] = useState<DriverRow | null>(null)

  /* -------- list query -------- */
  const queryKey = useMemo(
    () => [QUERY_NS, 'list', search, roleFilter, statusFilter, page] as const,
    [search, roleFilter, statusFilter, page],
  )

  const listQuery = useQuery({
    queryKey,
    queryFn: () => fetchUsers(api, search, roleFilter, statusFilter, page, PAGE_SIZE),
    placeholderData: (prev: PaginatedResponse<DriverRow> | undefined) => prev,
    staleTime: 30 * 1000,
  })

  /* -------- toggle active mutation -------- */
  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await api.patch<unknown>(`/api/users/${id}`, { active })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_NS] })
      setPendingToggle(null)
    },
  })

  const rows = listQuery.data?.data ?? []
  const total = listQuery.data?.meta.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text">Chauffeurs</h1>
          <p className="text-sm text-text-muted">
            Gérez les comptes chauffeurs et administrateurs.
          </p>
        </div>
        <Button onClick={() => setEditing(null)}>
          <Plus size={16} />
          Nouveau compte
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-6 flex flex-col gap-1.5">
              <Label htmlFor="users-search">Recherche</Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
                  aria-hidden="true"
                />
                <Input
                  id="users-search"
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Nom ou email…"
                  className="pl-9"
                />
                {searchDraft !== '' ? (
                  <button
                    type="button"
                    aria-label="Effacer"
                    onClick={() => {
                      setSearchDraft('')
                      setSearch('')
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-text-muted hover:text-text hover:bg-surface-hover"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="sm:col-span-3 flex flex-col gap-1.5">
              <Label htmlFor="users-role">Rôle</Label>
              <select
                id="users-role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                className={cn(
                  'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
                  'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
                )}
              >
                <option value="all">Tous</option>
                <option value="admin">Administrateurs</option>
                <option value="driver">Chauffeurs</option>
              </select>
            </div>

            <div className="sm:col-span-3 flex flex-col gap-1.5">
              <Label htmlFor="users-status">Statut</Label>
              <select
                id="users-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className={cn(
                  'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
                  'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
                )}
              >
                <option value="all">Tous</option>
                <option value="active">Actifs</option>
                <option value="inactive">Désactivés</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="py-16 flex justify-center">
            <Spinner size={28} label="Chargement des comptes" />
          </div>
        ) : listQuery.isError ? (
          <div className="px-6 py-12 text-center text-danger">
            Impossible de charger les comptes.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-text-muted">
            <UsersIcon size={28} className="mx-auto mb-2 opacity-60" aria-hidden="true" />
            <p>Aucun compte ne correspond aux filtres.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-text-muted">
                <tr className="text-left">
                  <Th>Nom</Th>
                  <Th>Email</Th>
                  <Th className="w-32">Rôle</Th>
                  <Th className="text-center w-32">Statut</Th>
                  <Th className="w-44">Dernière connexion</Th>
                  <Th className="w-12" srOnly>
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <UserRow
                    key={row.id}
                    row={row}
                    onEdit={() => setEditing(row)}
                    onToggleActive={() => setPendingToggle(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > 0 ? (
        <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
          <p>
            Affichage {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || listQuery.isFetching}
            >
              <ChevronLeft size={14} />
              Précédent
            </Button>
            <span className="px-2 text-text">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages || listQuery.isFetching}
            >
              Suivant
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      ) : null}

      <DriverDialog
        driver={editing}
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined)
        }}
      />

      <ConfirmDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingToggle(null)
            toggleMutation.reset()
          }
        }}
        title={
          pendingToggle?.active === true
            ? 'Désactiver le compte ?'
            : 'Réactiver le compte ?'
        }
        description={
          pendingToggle?.active === true
            ? "La personne ne pourra plus se connecter. Les missions et plannings historiques restent intacts."
            : 'La personne pourra à nouveau se connecter et apparaîtra dans les sélecteurs.'
        }
        confirmLabel={
          pendingToggle?.active === true ? 'Désactiver' : 'Réactiver'
        }
        tone={pendingToggle?.active === true ? 'warning' : 'primary'}
        loading={toggleMutation.isPending}
        errorMessage={
          toggleMutation.error instanceof ApiError
            ? toggleMutation.error.message
            : null
        }
        onConfirm={() => {
          if (pendingToggle !== null) {
            toggleMutation.mutate({
              id: pendingToggle.id,
              active: !pendingToggle.active,
            })
          }
        }}
      >
        {pendingToggle !== null ? (
          <div className="text-sm text-text">
            <p className="font-medium">{pendingToggle.name}</p>
            <p className="text-text-muted text-xs mt-1">
              {pendingToggle.email}
            </p>
          </div>
        ) : null}
      </ConfirmDialog>
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Row                                                                        */
/* -------------------------------------------------------------------------- */

interface UserRowProps {
  row: DriverRow
  onEdit: () => void
  onToggleActive: () => void
}

function UserRow({ row, onEdit, onToggleActive }: UserRowProps) {
  return (
    <tr className="border-t border-surface-border hover:bg-surface-hover transition-colors">
      <Td className="font-medium text-text">{row.name}</Td>
      <Td className="text-text-muted">{row.email}</Td>
      <Td>
        <RoleBadge role={row.role} />
      </Td>
      <Td className="text-center">
        {row.active ? (
          <Badge tone="success">Actif</Badge>
        ) : (
          <Badge tone="danger">Désactivé</Badge>
        )}
      </Td>
      <Td className="text-text-muted">
        {row.lastLoginAt !== null ? formatDateTime(row.lastLoginAt) : 'Jamais'}
      </Td>
      <Td className="w-12 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Actions pour ${row.name}`}
              className="p-1.5 rounded-md text-text-muted hover:bg-surface-hover hover:text-text"
            >
              <MoreVertical size={16} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit()}>
              <Pencil size={14} aria-hidden="true" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem
              danger={row.active}
              onSelect={() => onToggleActive()}
            >
              <Power size={14} aria-hidden="true" />
              {row.active ? 'Désactiver' : 'Réactiver'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Td>
    </tr>
  )
}

function RoleBadge({ role }: { role: DriverRow['role'] }) {
  switch (role) {
    case 'super_admin':
      return <Badge tone="template">Super admin</Badge>
    case 'admin':
      return <Badge tone="info">Administrateur</Badge>
    case 'driver':
    default:
      return <Badge tone="neutral">Chauffeur</Badge>
  }
}

/* -------------------------------------------------------------------------- */
/*  Table cell helpers                                                         */
/* -------------------------------------------------------------------------- */

function Th({
  className,
  children,
  srOnly = false,
}: {
  className?: string
  children: React.ReactNode
  srOnly?: boolean
}) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 font-medium uppercase tracking-wide text-xs',
        className,
      )}
    >
      {srOnly ? <span className="sr-only">{children}</span> : children}
    </th>
  )
}

function Td({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <td className={cn('px-3 py-2.5', className)}>{children}</td>
}

/* -------------------------------------------------------------------------- */
/*  Fetcher                                                                    */
/* -------------------------------------------------------------------------- */

async function fetchUsers(
  api: ApiClient,
  search: string,
  roleFilter: RoleFilter,
  statusFilter: StatusFilter,
  page: number,
  limit: number,
): Promise<PaginatedResponse<DriverRow>> {
  const params: Record<string, string | number | boolean> = {
    page,
    limit,
  }
  if (roleFilter !== 'all') params.role = roleFilter
  if (statusFilter === 'active') params.active = true
  if (statusFilter === 'inactive') params.active = false
  // The /api/users API doesn't expose a `search` param yet —
  // we post-filter the page on name/email match. With limit=20
  // this is cheap; if drivers grow past a few hundred per
  // tenant we'll add server-side search.
  const result = await api.getPaginated<DriverRow>('/api/users', params)
  if (search.trim() === '') return result
  const needle = search.trim().toLowerCase()
  return {
    ...result,
    data: result.data.filter(
      (u) =>
        u.name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle),
    ),
    meta: {
      ...result.meta,
      total: result.data.filter(
        (u) =>
          u.name.toLowerCase().includes(needle) ||
          u.email.toLowerCase().includes(needle),
      ).length,
    },
  }
}
